/**
 * EscaladeDetail — the working surface for ONE D-29 escalation, rendered in a Drawer. Composes:
 *   - the escalade header (statut, canal, tentatives, next silence-sweep),
 *   - the attempts journal (read-only ledger),
 *   - the ladder-attempt form (record a push/call — advances the sweep),
 *   - the read-back acknowledgment form (CLOSES the loop — VAL-2),
 *   - the break-glass trigger (VAL-3, duty-to-warn).
 *
 * The server exposes no GET-escalade endpoint, so this panel is seeded with whatever we hold (an id,
 * optionally a fuller object from a prior mutation) and re-hydrates from each mutation's return value
 * (acknowledge / recordTentative both return the updated EscaladeCritique). Every action is gated on
 * `validation.valeurcritique.acknowledge`; an un-capable user sees a read-only panel.
 */
import { useState } from "react";
import { Badge, Button, Card, Drawer, Table, useToast, type Column } from "@/ui";
import { useT } from "@/app";
import { useCan } from "@/auth";
import type { ApiError } from "@/api";
import { token } from "@/design/theme";
import {
  CAP,
  type EscaladeCritique,
  type EscaladeTentative,
  type Id,
} from "../types";
import { useAcknowledgeEscalade, useInvokeBreakGlass, useRecordTentative } from "../hooks/useValidation";
import { EscaladeStatusBadge } from "./Badges";
import { ReadBackForm } from "./ReadBackForm";
import { TentativeForm } from "./TentativeForm";
import { BreakGlassModal } from "./BreakGlassModal";

export interface EscaladeDetailProps {
  open: boolean;
  onClose: () => void;
  escaladeId: Id;
  resultatId?: Id | null;
  /** A fuller object if we already have one (from a prior mutation). */
  initial?: EscaladeCritique | null;
}

function fmt(dt?: string | null): string {
  if (!dt) return "—";
  const d = new Date(dt);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

export function EscaladeDetail({ open, onClose, escaladeId, resultatId, initial }: EscaladeDetailProps) {
  const { t } = useT();
  const toast = useToast();
  const canAck = useCan(CAP.acknowledge);

  // Locally-hydrated escalade — re-seeded from each mutation's return.
  const [escalade, setEscalade] = useState<EscaladeCritique | null>(initial ?? null);
  const [attempts, setAttempts] = useState<EscaladeTentative[]>(initial?.essais ?? []);
  const [bgOpen, setBgOpen] = useState(false);

  const acknowledge = useAcknowledgeEscalade();
  const recordTentative = useRecordTentative();
  const breakGlass = useInvokeBreakGlass();

  const statut = escalade?.statut ?? "a_acquitter";
  const closed = statut === "acquitte";

  const onAck = async (input: { read_back_text: string; acquitte_par?: string }) => {
    try {
      const updated = await acknowledge.mutateAsync({ id: escaladeId, ...input });
      setEscalade(updated);
      if (updated.essais) setAttempts(updated.essais);
      toast.success(t("validation.ack.ok"));
    } catch (e) {
      const err = e as ApiError;
      toast.error(err.code === "VALIDATION" ? t("validation.ack.required") : err.message);
    }
  };

  const onTentative = async (input: {
    canal: EscaladeCritique["canal"];
    resultat: EscaladeTentative["resultat"];
    notification_ref?: string;
  }) => {
    try {
      const created = await recordTentative.mutateAsync({ id: escaladeId, ...input });
      setAttempts((a) => [created, ...a]);
      setEscalade((prev) =>
        prev ? { ...prev, tentatives: prev.tentatives + 1, canal: input.canal } : prev,
      );
      toast.success(t("validation.tentative.ok"));
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  };

  const onBreakGlass = async (input: { justification: string; medecin_ref?: Id }) => {
    try {
      await breakGlass.mutateAsync({ id: escaladeId, ...input });
      toast.warn(t("validation.breakglass.ok"));
      setBgOpen(false);
    } catch (e) {
      const err = e as ApiError;
      toast.error(err.code === "VALIDATION" ? t("validation.breakglass.required") : err.message);
    }
  };

  const attemptCols: Column<EscaladeTentative>[] = [
    {
      key: "canal",
      header: t("validation.escalade.attempts.channel"),
      cell: (a) => t(`validation.escalade.canal.${a.canal}`),
    },
    {
      key: "resultat",
      header: t("validation.escalade.attempts.outcome"),
      cell: (a) => <Badge tone={a.resultat === "echec_technique" ? "danger" : "neutral"}>
        {t(`validation.tentative.resultat.${a.resultat}`)}
      </Badge>,
    },
    {
      key: "when",
      header: t("validation.escalade.attempts.when"),
      mono: true,
      cell: (a) => <span className="nv-mono nv-ltr-island">{fmt(a.tente_at)}</span>,
    },
  ];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={
        <span>
          {t("validation.escalade.fromResult", { id: String(resultatId ?? escalade?.resultat_id ?? "—") })}
        </span>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: token.s5 }}>
        {/* Header facts */}
        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr",
            gap: `${token.s2} ${token.s5}`,
            margin: 0,
          }}
        >
          <dt style={{ color: token.slate }}>{t("validation.escalade.statut")}</dt>
          <dd style={{ margin: 0 }}>
            <EscaladeStatusBadge statut={statut} />
          </dd>
          <dt style={{ color: token.slate }}>{t("validation.escalade.canal")}</dt>
          <dd style={{ margin: 0 }}>
            {escalade ? t(`validation.escalade.canal.${escalade.canal}`) : "—"}
          </dd>
          <dt style={{ color: token.slate }}>{t("validation.escalade.tentatives")}</dt>
          <dd className="nv-mono nv-ltr-island" style={{ margin: 0 }}>
            {escalade?.tentatives ?? attempts.length}
          </dd>
          <dt style={{ color: token.slate }}>{t("validation.escalade.nextSweep")}</dt>
          <dd className="nv-mono nv-ltr-island" style={{ margin: 0 }}>
            {fmt(escalade?.prochaine_tentative_at)}
          </dd>
        </dl>

        {closed && escalade?.read_back_text && (
          <Card title={t("validation.ack.readBack")}>
            <blockquote style={{ margin: 0, color: token.ink }}>{escalade.read_back_text}</blockquote>
            <p style={{ margin: 0, marginBlockStart: token.s2, fontSize: token.fsCaption, color: token.steel }}>
              {t("validation.ack.by")}: {escalade.acquitte_par ?? "—"} · {fmt(escalade.acquitte_at)}
            </p>
          </Card>
        )}

        {!canAck ? (
          <Badge tone="warn">{CAP.acknowledge}</Badge>
        ) : closed ? null : (
          <>
            <Card title={t("validation.ack.title")}>
              <ReadBackForm onSubmit={onAck} submitting={acknowledge.isPending} />
            </Card>

            <Card title={t("validation.tentative.title")}>
              <TentativeForm
                defaultCanal={escalade?.canal ?? "telephone"}
                onSubmit={onTentative}
                submitting={recordTentative.isPending}
              />
            </Card>

            <Card title={t("validation.breakglass.title")}>
              <p style={{ margin: 0, marginBlockEnd: token.s3, color: token.slate, fontSize: token.fsLabel }}>
                {t("validation.breakglass.desc")}
              </p>
              <Button variant="danger" onClick={() => setBgOpen(true)}>
                {t("validation.breakglass.open")}
              </Button>
            </Card>
          </>
        )}

        {/* Attempts ledger */}
        <Card title={t("validation.escalade.attempts.title")} padded={false}>
          <Table<EscaladeTentative>
            columns={attemptCols}
            rows={attempts}
            rowKey={(a) => String(a.id)}
            caption={t("validation.escalade.attempts.title")}
            emptyTitle={t("validation.escalade.attempts.empty")}
          />
        </Card>
      </div>

      <BreakGlassModal
        open={bgOpen}
        onClose={() => !breakGlass.isPending && setBgOpen(false)}
        onConfirm={onBreakGlass}
        submitting={breakGlass.isPending}
      />
    </Drawer>
  );
}
